(function (global) {
  const HOLD = () => (global.HSMotionHold == null ? 0.12 : global.HSMotionHold);

  function clamp(n, a, b) {
    return Math.min(b, Math.max(a, n));
  }

  function chapterAt(progress, chapters) {
    const p = clamp(progress, 0, 1);
    for (let i = 0; i < chapters.length; i += 1) {
      const [start, end] = chapters[i].progress;
      if (p <= end || i === chapters.length - 1) return { chapter: chapters[i], index: i };
    }
    return { chapter: chapters[chapters.length - 1], index: chapters.length - 1 };
  }

  function localProgress(progress, chapter) {
    const [start, end] = chapter.progress;
    const span = Math.max(0.0001, end - start);
    return clamp((progress - start) / span, 0, 1);
  }

  function progressToTime(progress, chapters, duration) {
    const { chapter } = chapterAt(progress, chapters);
    const local = localProgress(progress, chapter);
    const hold = HOLD();
    const usable = clamp((local - hold) / Math.max(0.0001, 1 - hold * 2), 0, 1);
    const [a, b] = chapter.video;
    return clamp(a + (b - a) * usable, 0, duration);
  }

  function copyVisibility(local) {
    const hold = HOLD();
    if (local < hold) return clamp(local / hold, 0, 1);
    if (local > 1 - hold) return clamp((1 - local) / hold, 0, 1);
    return 1;
  }

  class VideoScrubber {
    constructor(video) {
      this.video = video;
      this.ready = false;
      this.last = -1;
      this._onReady = () => { this.ready = true; };
      video.addEventListener('loadedmetadata', this._onReady);
      video.addEventListener('canplay', this._onReady);
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      if (!video.getAttribute('preload') || video.getAttribute('preload') === 'none') {
        video.preload = 'metadata';
      }
      video.controls = false;
      video.disablePictureInPicture = true;
    }

    duration() {
      const d = this.video.duration;
      return Number.isFinite(d) && d > 0 ? d : (global.HSMotionDuration || 14.95);
    }

    seek(time) {
      if (!this.ready) return;
      const t = clamp(time, 0, this.duration() - 0.04);
      if (Math.abs(t - this.last) < 0.045) return;
      this.last = t;
      try {
        if (typeof this.video.fastSeek === 'function') this.video.fastSeek(t);
        else this.video.currentTime = t;
      } catch (_) {
        try { this.video.currentTime = t; } catch (__) {}
      }
    }

    destroy() {
      this.video.removeEventListener('loadedmetadata', this._onReady);
      this.video.removeEventListener('canplay', this._onReady);
    }
  }

  class CanvasSequence {
    constructor(canvas, posters) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.posters = posters || [];
      this.images = [];
      this.ready = false;
      this.last = -1;
      this._dpr = Math.min(window.devicePixelRatio || 1, 2);
    }

    async load() {
      const loaded = await Promise.all(this.posters.map((src) => new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      })));
      this.images = loaded.filter(Boolean);
      this.ready = this.images.length > 0;
      this.resize();
    }

    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width * this._dpr));
      const h = Math.max(1, Math.round(rect.height * this._dpr));
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
    }

    draw(progress, chapters) {
      if (!this.ready) return;
      const { index } = chapterAt(progress, chapters);
      if (index === this.last) return;
      this.last = index;
      this.resize();
      const img = this.images[Math.min(index, this.images.length - 1)];
      if (!img) return;
      const { width, height } = this.canvas;
      const ir = img.width / img.height;
      const cr = width / height;
      let dw = width;
      let dh = height;
      let dx = 0;
      let dy = 0;
      if (ir > cr) {
        dh = height;
        dw = height * ir;
        dx = (width - dw) / 2;
      } else {
        dw = width;
        dh = width / ir;
        dy = (height - dh) / 2;
      }
      this.ctx.clearRect(0, 0, width, height);
      this.ctx.drawImage(img, dx, dy, dw, dh);
    }

    destroy() {
      this.images = [];
    }
  }

  global.HSMotionEngines = {
    clamp,
    chapterAt,
    localProgress,
    progressToTime,
    copyVisibility,
    VideoScrubber,
    CanvasSequence
  };
})(window);
